<template lang="pug">
.uc-toggle-included.z-100.auto-alpha-0(:class="[isEditModeActive ? 'auto-alpha-1' : '']")
  transition-fade(:duraion="0.3")
    .absolute.inset-0.m-0.flex.items-center.justify-center.bg-gray-500.bg-opacity-20.auto-alpha-0(
      :class="[!included ? 'auto-alpha-1 translate-y-0' : ' ']"
    )
      .fill.opacity-30.hatching
      .sticky.my-10.bg-slate-200.center.p-10.inset-10.text-xl.rounded-full.px-20.auto-alpha-0(
        :class="[!included ? 'auto-alpha-1 translate-y-0' : 'translate-y-6']"
        v-if="props.type === 'section'"
      )
        span You have excluded this section.
        | #{ ' ' }
        button.font-bold.text-primary-500.fancy-hover(@click="included = true") Undo

  //- toggle units (on dashboard screens)
  label.absolute(
    :class="[props?.screen?.cards ? 'top-2 right-2 text-sm dark_text-white select-none py-2 px-4 rounded-full flex items-center gap-3 group dark_bg-black/70 bg-white/70' : 'top-[33.3%] right-2', currentPageIsActive && 'opacity-80']"
    ref="elRef"
    v-if="props.type === 'unit'"
  )
    span.group-hover_opacity-100.transition(
      :class="currentPageIsActive ? 'opacity-70 italic' : 'opacity-80 ms-1'"
      v-if="props?.screen?.cards"
    ) {{ currentPageIsActive ? 'Current unit' : included ? 'Included' : 'Excluded' }}
    | #{ ' ' }
    input-toggle(v-if="!currentPageIsActive" v-model="included")

  //- toggle sections
  label.absolute.top-12.inline-block.text-sm.dark_text-white.select-none.py-2.px-4.rounded-full.flex.items-center.gap-3.group(
    :class="[ hasDelete ? 'right-16' : 'right-5', 'dark_bg-black/70 bg-white/70']"
    ref="elRef"
    v-else-if="props.type === 'section'"
  )
    //- expose for translations
    //- text="Page included"
    //- text="Page excluded"
    span.ms-1.opacity-80.group-hover_opacity-100.transition {{ included ? 'Page included' : 'Page excluded' }}
    | #{ ' ' }
    input-toggle(v-model="included")
</template>

<script setup lang="ts">
import { computed, inject, ref, unref, watch, watchEffect } from 'vue';
import useContent from '../stores/useContent';
import { setCompleted } from '../lib/SCORM';
import getEditorWindow from '../lib/getEditorWindow';

const currentLocation = unref(inject('currentLocation'));
const isEditModeActive = inject('isEditModeActive') as Ref<boolean>;
const props = defineProps<{
  screen: UcScreen;
  unit: Page;
  type: string;
  id: string;
  hasDelete?: boolean;
}>();
const content = useContent();
const included = ref(!content.compiled.hidden?.[props.id]);
const elRef = ref<HTMLElement>();

const currentPageIsActive = computed(() => {
  return props.id === currentLocation;
});

watch(included, value => {
  // Resolve the authoring host lazily (see getEditorWindow) — its presence is the
  // authoring gate. Resolved at toggle time, not import time, so it sees the host's
  // runtime-assigned `acceptHiddenFromPreview` handler.
  const hostWindow = getEditorWindow('acceptHiddenFromPreview');
  if (hostWindow?.acceptHiddenFromPreview) {
    // In authoring, update the local store synchronously so the preview
    // (grayscale on hidden screens) reflects immediately and the reconciliation
    // watcher below doesn't revert `included` before the async YAML round-trip
    // lands. Then persist the change into the Monaco YAML draft — the draft's
    // source of truth. The default path (updateValue -> commit ->
    // updateCustomClientAPI) only saves to the draft when clientEditMode is set,
    // which it isn't in authoring, so it can't persist on its own here.
    content.setEditValue(`hidden.${props.id}`, !value);
    hostWindow.acceptHiddenFromPreview(props.id, !value);
  } else {
    content.updateValue(`hidden.${props.id}`, !value);
  }

  // unlock the unit/section if we exclude it, so we can progress past it
  if (!value) {
    setCompleted(props.id);

    const page = elRef.value?.closest('.page') as UcPageElement;
    page.refreshSections();
  }
});

watchEffect(() => {
  // update this toggle state if something else changes the hidden state for this ID (e.g. multiple dashboards in one page)
  if (!content.compiled.hidden?.[props.id] !== included.value) {
    included.value = !content.compiled.hidden?.[props.id];
  }
});
</script>
